import { GroundTruthData } from "src/store/modelLifeCycleGroundTruthSlice";
import apiClient from "src/utils/apiClient";
import { Accuracy, Count } from "../components/pages/automatedJobs/types";
import { createToast } from "src/store/toastsSlice";
import { TOAST_TIMEOUT } from "src/config/Toast";
import store from "src/store/store";
import { TimePeriodType } from "src/services/timePeriodService";
import { AutomatedJobFormData } from "src/components/pages/automatedJobs/components/automatedJobForm";

export interface DataSource {
  name: string;
  source_table_name: string;
  source_id_column_name: string;
  source_text_expression: string;
  timestamp_column_name: string;
}

export interface BatchJobDefinition {
  id?: string;
  name: string;
  modelId: string;
  ownerAlias?: string;
  status?: string;
  source: DataSource;
  source_data_filter_clause: string;
  destination: {
    tableName: string;
    id_column_name: string;
  };
  version?: number;
  updatedBy?: string;
}

export interface GetAccuracyResDTO {
  accuracy: Record<string, number>;
  averageAccuracy: number;
}

export type GetCountsResDTO = {
  period_start: string;
  period_end: string;
  reviewed_count: number;
  total_count: number;
}[];

export interface CommonGetReqDTO {
  definition_id: string;
  start_date: string;
  end_date: string;
}

export interface GetCountsReqDTO extends CommonGetReqDTO {
  period: TimePeriodType;
}

export interface UpdateInsightsDTO {
  definition_id: string;
  ground_truth_data: GroundTruthData;
  is_selected: boolean;
}

export interface GetInsightBySourceIdDTO {
  definition_id: string;
  source_id: string;
}

export interface ValidateFilterClauseResponse {
  is_valid: boolean;
  row_count: number;
}

class AutomatedJobsService {
  prefix = "/automated_job";

  async getAutomatedJobs() {
    const { data } = await apiClient.get<BatchJobDefinition[]>(`${this.prefix}/job_definitions`);
    return data;
  }

  async createAutomatedJob(jobData: AutomatedJobFormData): Promise<BatchJobDefinition> {
    const requestData: BatchJobDefinition = {
      name: jobData.name,
      modelId: jobData.modelId,
      source: jobData.source,
      source_data_filter_clause: jobData.source_data_filter_clause,
      destination: jobData.destination,
    };

    const { data } = await apiClient.put<BatchJobDefinition>(
      `${this.prefix}/save_definition`,
      requestData,
    );

    // Show success toast
    store.dispatch(
      createToast({
        type: "success",
        message: "Automated job created successfully!",
        timeout: TOAST_TIMEOUT,
      }),
    );

    return data;
  }

  async updateAutomatedJob(
    id: string,
    jobData: Partial<AutomatedJobFormData>,
  ): Promise<BatchJobDefinition> {
    // For updates, we need to pass the complete BatchJobDefinition object
    // First get the existing job, then update the specified fields
    const existingJobs = await this.getAutomatedJobs();
    const existingJob = existingJobs.find((job) => job.id === id);

    if (!existingJob) {
      throw new Error(`Job with id ${id} not found`);
    }

    const requestData: BatchJobDefinition = {
      ...existingJob,
      ...jobData, // Override with new values
    };

    const { data } = await apiClient.put<BatchJobDefinition>(
      `${this.prefix}/save_definition`,
      requestData,
    );

    // Show success toast
    store.dispatch(
      createToast({
        type: "success",
        message: "Automated job updated successfully!",
        timeout: TOAST_TIMEOUT,
      }),
    );

    return data;
  }

  async getRandomInsight(params: CommonGetReqDTO) {
    const { data } = await apiClient.get<GroundTruthData>(`${this.prefix}/random_insight`, {
      params,
    });
    return data;
  }

  async getAccuracy(params: CommonGetReqDTO): Promise<Accuracy> {
    const { data } = await apiClient.get<GetAccuracyResDTO>(`${this.prefix}/accuracy`, {
      params,
    });
    return data;
  }

  async getCounts(params: GetCountsReqDTO): Promise<Count[]> {
    const { data } = await apiClient.get<GetCountsResDTO>(`${this.prefix}/counts`, {
      params,
    });
    return data
      .map((count) => ({
        periodStart: new Date(count.period_start),
        periodEnd: new Date(count.period_end),
        reviewedCount: count.reviewed_count,
        totalCount: count.total_count,
      }))
      .sort((a, b) => (a.periodStart > b.periodStart ? -1 : 1));
  }

  async updateInsight(params: UpdateInsightsDTO): Promise<GroundTruthData> {
    const urlParams = new URLSearchParams({
      definition_id: params.definition_id,
      is_selected: params.is_selected.toString(), // Always send as boolean string
    });

    const { data } = await apiClient.put<GroundTruthData>(
      `${this.prefix}/update?${urlParams.toString()}`,
      params.ground_truth_data,
    );
    if (data) {
      // Show success toast with different message based on selection type
      const message = params.is_selected
        ? "Selected insight updated successfully. Navigating to next random insight..."
        : "Insight updated successfully. Navigating to next random insight...";

      store.dispatch(
        createToast({
          type: "success",
          message,
          timeout: TOAST_TIMEOUT,
        }),
      );
    }
    return data;
  }

  async getInsightBySourceId(params: GetInsightBySourceIdDTO): Promise<GroundTruthData> {
    const { data } = await apiClient.get<GroundTruthData>(`${this.prefix}/insight_by_source_id`, {
      params,
    });
    return data;
  }

  async getDataSources(): Promise<DataSource[]> {
    const { data } = await apiClient.get<DataSource[]>(`${this.prefix}/data_sources`);
    return data;
  }

  async validateFilterClause(
    dataSource: DataSource,
    filterClause: string,
    jobId?: string,
  ): Promise<ValidateFilterClauseResponse> {
    try {
      const { data } = await apiClient.post<ValidateFilterClauseResponse>(
        `${this.prefix}/validate_filter_clause`,
        {
          data_source: dataSource,
          filter_clause: filterClause,
          job_id: jobId,
        },
      );
      return data;
    } catch (error: any) {
      // Re-throw with proper error message extraction
      const errorMessage = error?.response?.data?.detail || error?.message || "Validation failed";
      throw new Error(errorMessage);
    }
  }
}

export default new AutomatedJobsService();
